import { useState } from 'react';
import PropTypes from 'prop-types';
import AddressMixin from 'mixins/address-mixin';
import HelpTip from 'components/tooltip/helptip';
import EmptyStateMessage from 'components/lists/empty-state-message';
import Icon from 'components/elements/icon';

/**
 * Formats URL query parameters from an object
 */
const formatQueryParams = (params) => {
  return new URLSearchParams(params).toString();
};

/**
 * Checks if an object is empty
 */
const isEmpty = (obj) => {
  return obj == null || Object.keys(obj).length === 0;
};

/**
 * Cleans up address strings by removing leading/trailing commas and extra spaces
 */
const cleanAddressString = (str) => {
  if (!str) return '';
  return str
    .replace(/^[,\s]+|[,\s]+$/g, '') // Remove leading/trailing commas and spaces
    .replace(/,\s*,/g, ',') // Remove duplicate commas
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim();
};

/**
 * AddressCard component displays address information with Google Street View imagery
 * and census data for home values and median income
 */
const AddressCard = ({ address = {} }) => {
  const [imageError, setImageError] = useState(false);
  /**
   * Renders a value or a dash for missing data
   */
  const renderValue = (display) => {
    if (isEmpty(address) || display === 'Not Available' || !display) {
      return <span className="address-card--empty">—</span>;
    }
    return <span className="fs-exclude-data address-data">{display}</span>;
  };

  const is_empty = isEmpty(address);
  const formattedAddress = AddressMixin.formatAddressObject(address);

  const imageParams = {
    size: '640x175',
    location: `${formattedAddress.line_1 || formattedAddress.address_1} ${formattedAddress.city}, ${
      formattedAddress.state
    } ${formattedAddress.city_state_zip || formattedAddress.postal_zip}`,
    sensor: false,
    key: 'AIzaSyDGNa0vJIdeMUYAsIpnFAPbZefWJ8RlVhY',
  };

  return (
    <div className="address-card">
      <div className="address-card--body">
        <div className="address-card--google-img">
          {is_empty || imageError ? (
            <EmptyStateMessage icon="home" text="No imagery available" />
          ) : (
            <img
              className="fs-exclude-data"
              src={`https://maps.googleapis.com/maps/api/streetview?${formatQueryParams(imageParams)}`}
              alt="Google Street View"
              onError={() => setImageError(true)}
            />
          )}
        </div>

        <div className="address-card--details">
          {is_empty ? (
            <div className="address-card--empty">Not Provided</div>
          ) : (
            <>
              <div className="address-card--address-container">
                <div className="address-card--header">
                  <div className="address-card--header-left">
                    <Icon className="address-card--location-icon" icon="location" />
                    <span className="address-card--type">{formattedAddress.type || 'Address'}</span>
                  </div>
                  <div className="address-card--header-right">
                    <HelpTip help_key="est_home_value" />
                  </div>
                </div>
                <div className="fs-exclude-data address-card--address">{formattedAddress.line_1}</div>
                <div className="fs-exclude-data address-card--address">
                  {cleanAddressString(formattedAddress.city_state_zip)}
                  {formattedAddress.source === 'fullcontact' && (
                    <Icon className="address-card--enrichment-icon" icon="et-pyramid" size={1} />
                  )}
                </div>
              </div>

              <div className="address-card--meta-data">
                <div className="address-card--meta-item">
                  <span className="address-card--meta-label">Neighborhood Median Home Value:</span>{' '}
                  {renderValue(formattedAddress.median_home)}
                </div>
                <div className="address-card--meta-item">
                  <span className="address-card--meta-label">Value Range:</span> {renderValue(formattedAddress.range)}
                </div>
                <div className="address-card--meta-item address-card--meta-income">
                  <span className="address-card--meta-label">Median Income:</span>{' '}
                  {renderValue(formattedAddress.median_income)}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

AddressCard.propTypes = {
  address: PropTypes.object,
};

export default AddressCard;
